import { Button, SolvTheme, Stack, Text } from '@solvhealth/jigsaw-v1';
import Booking from '@solvhealth/types/interfaces/Booking';
import Location from '@solvhealth/types/interfaces/Location';
import isEmpty from 'lodash/isEmpty';
import React, { useCallback } from 'react';
import ReviewHeader from '../../../components/AddReview/ReviewHeader';
import { PRIMARY_RATING } from '../../../components/LeaveAReview/components/PrimaryRatingSelector';
import useFetchSingleProvider from '../../../components/ProviderGroup/hooks/useFetchSingleProvider';
import { getProviderName } from '../../../components/ProviderGroup/Provider/util';
import {
  ADD_REVIEW_DIRECT_TO_SOLV,
  ADD_REVIEW_FLOW_RECOMMEND_TO_GOOGLE,
  ADD_REVIEW_INITIAL,
  ADD_REVIEW_TO_SOLV,
  DO_YOU_HAVE_GOOGLE_ACCOUNT,
} from '../../../constants';
import { analyticsTrackEvent } from '../../../core/analytics';
import {
  ADD_REVIEW_I_WOULD_NOT_RECOMMEND,
  ADD_REVIEW_I_WOULD_RECOMMEND,
} from '../../../core/analytics/events';
import { safeGet } from '../../../core/util/object';
import AddReviewWarning from '../AddReviewWarning';
import LandingTransition from '../transitions/LandingTransition';

type Props = {
  bookingId: string;
  booking: Booking;
  location: Location;
  handleRatingChange: (...args: any[]) => any;
  submitReview: (...args: any[]) => any;
  setFlowState: (...args: any[]) => any;
  review?: string;
  error?: any;
  flowState: string;
  starRating?: number;
  onStarChange: (...args: any[]) => any;
  handleHasRecommendedClinicChange: (...args: any[]) => any;
};

const WouldYouRecommendCollection = (props: Props) => {
  const {
    bookingId,
    booking,
    location,
    handleRatingChange,
    submitReview,
    setFlowState,
    review,
    error,
    flowState,
    starRating,
    onStarChange,
    handleHasRecommendedClinicChange,
  } = props;

  const { provider } = useFetchSingleProvider(booking?.providerId);

  const handleWouldYouRecommendYes = useCallback(() => {
    const trackingProperties = {
      locationId: location.id,
      flowType: ADD_REVIEW_FLOW_RECOMMEND_TO_GOOGLE,
      starRating,
      review,
      bookingId,
    };
    analyticsTrackEvent(ADD_REVIEW_I_WOULD_RECOMMEND, trackingProperties);

    handleHasRecommendedClinicChange(true);

    const isDirectToSolvReviewFlow = () =>
      safeGet(location)('reviewFlowType') === ADD_REVIEW_DIRECT_TO_SOLV;

    setFlowState(isDirectToSolvReviewFlow() ? ADD_REVIEW_TO_SOLV : DO_YOU_HAVE_GOOGLE_ACCOUNT);
  }, [
    booking?.providerId,
    bookingId,
    handleHasRecommendedClinicChange,
    handleRatingChange,
    location,
    onStarChange,
    review,
    setFlowState,
    starRating,
    submitReview,
  ]);

  const handleWouldYouRecommendNo = useCallback(() => {
    const trackingProperties = {
      locationId: location.id,
      flowType: ADD_REVIEW_FLOW_RECOMMEND_TO_GOOGLE,
      starRating,
      review,
      bookingId,
    };

    onStarChange({ value: 3, genre: PRIMARY_RATING });
    analyticsTrackEvent(ADD_REVIEW_I_WOULD_NOT_RECOMMEND, trackingProperties);

    submitReview(
      {
        locationId: location.id,
        bookingId,
        providerId: booking?.providerId,
        starRating: 3,
        hasRecommendedClinic: false,
      },
      false
    );

    handleHasRecommendedClinicChange(false);

    handleRatingChange(3);

    setFlowState(ADD_REVIEW_TO_SOLV);
  }, [
    booking?.providerId,
    bookingId,
    handleHasRecommendedClinicChange,
    handleRatingChange,
    location.id,
    onStarChange,
    review,
    setFlowState,
    starRating,
    submitReview,
  ]);

  const renderWouldYouRecommend = useCallback(() => {
    const copy = provider ? (
      <>
        Would you recommend <br /> {getProviderName(provider, false)} <br /> at {location.name}?
      </>
    ) : (
      <>
        Would you recommend <br /> {location.name}?
      </>
    );

    return (
      <Stack>
        <ReviewHeader booking={booking} location={location} reviewStatus={flowState} />
        <AddReviewWarning error={error} ratingDate={booking?.ratingDate} />

        {!isEmpty(location) && (
          <Stack
            alignItems="center"
            maxWidth={SolvTheme.breakpoints.sm}
            mx="auto"
            px={2}
            py={4}
            space={4}
            textAlign="center"
          >
            <Text variant="h2">{copy}</Text>

            <Stack alignItems="center" row space={1}>
              <Button onClick={handleWouldYouRecommendYes} variant="outlined">
                Yes
              </Button>
              <Button onClick={handleWouldYouRecommendNo} variant="outlined">
                No
              </Button>
            </Stack>
          </Stack>
        )}
      </Stack>
    );
  }, [
    booking,
    error,
    flowState,
    handleWouldYouRecommendNo,
    handleWouldYouRecommendYes,
    location,
    provider,
  ]);

  return (
    <LandingTransition
      flowState={flowState}
      stateToActivateFlow={ADD_REVIEW_INITIAL}
      toRender={renderWouldYouRecommend}
    />
  );
};

export default WouldYouRecommendCollection;
